const DATA_ENGINE_URL = window._env_?.REACT_APP_DATA_ENGINE_URL || 'http://localhost:8002';
const AGENT_URL = window._env_?.REACT_APP_TEMPLATE_AGENT_URL || 'http://localhost:8001';

export const dataEngineApi = {
  getRealms: () => fetch(`${DATA_ENGINE_URL}/api/query/realms`).then(r => r.json()),
  getSyncStatus: (rfId) => fetch(`${DATA_ENGINE_URL}/api/sync-status/${rfId}`).then(r => r.json()),

  streamChat: (body, signal) => fetch(`${AGENT_URL}/chat/data-query`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'text/event-stream' },
    body: JSON.stringify(body),
    signal,
  }),

  getThreads: (userId = 'admin') =>
    fetch(`${AGENT_URL}/threads?user_id=${userId}&agent=data-query`).then(r => r.json()),
  getThread: (threadId, userId = 'admin') =>
    fetch(`${AGENT_URL}/threads/${threadId}?user_id=${userId}`).then(r => r.json()),

  getModels: () => fetch(`${AGENT_URL}/models`).then(r => r.json()),

  triggerSync: (idOrRfId) => fetch(`${DATA_ENGINE_URL}/api/pipeline/sync`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ rf_id: idOrRfId }),
  }).then(r => r.json()),

  getCompanySources: () => fetch(`${DATA_ENGINE_URL}/api/company-sources`).then(r => r.json()),

  getPipelineJobs: (skip = 0, limit = 50, status = null, { companySourceId, rfId } = {}) => {
    let url = `${DATA_ENGINE_URL}/api/pipeline/jobs?skip=${skip}&limit=${limit}`;
    if (status) url += `&status=${status}`;
    if (companySourceId) url += `&company_source_id=${companySourceId}`;
    if (rfId) url += `&rf_id=${rfId}`;
    return fetch(url).then(r => r.json());
  },

  getPipelineLog: (jobId) => fetch(`${DATA_ENGINE_URL}/api/pipeline/${jobId}`).then(r => r.json()),

  // Reconciliation
  reconcile: (rfId, startDate, endDate) => fetch(`${DATA_ENGINE_URL}/api/reconcile/${rfId}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ start_date: startDate || null, end_date: endDate || null }),
  }).then(r => r.json()),

  // Entity Sync
  getEntitySyncCompanies: (page = 0, size = 50, search = '') => {
    let url = `${DATA_ENGINE_URL}/api/entity-sync/companies?page=${page}&size=${size}`;
    if (search) url += `&search=${encodeURIComponent(search)}`;
    return fetch(url).then(r => r.json());
  },
  getEntitySyncStatus: (rfId) => fetch(`${DATA_ENGINE_URL}/api/entity-sync/status/${rfId}`).then(r => r.json()),
  triggerEntityCdc: (rfId, entityType) => fetch(`${DATA_ENGINE_URL}/api/entity-sync/trigger/${rfId}/${entityType}`, { method: 'POST' }).then(r => r.json()),
  triggerEntityFullSync: (rfId, entityType) => fetch(`${DATA_ENGINE_URL}/api/entity-sync/full-sync/${rfId}/${entityType}`, { method: 'POST' }).then(r => r.json()),

  // Entity Data
  getEntityData: (entityType, rfId, page = 0, size = 50, search = '') => {
    let url = `${DATA_ENGINE_URL}/api/entities/${entityType}/${rfId}?page=${page}&size=${size}`;
    if (search) url += `&search=${encodeURIComponent(search)}`;
    return fetch(url).then(r => r.json());
  },

  // Skills (direct PostgreSQL via template-agent)
  getSkills: (userId) => fetch(`${AGENT_URL}/skills${userId ? `?user_id=${userId}` : ''}`).then(r => r.json()),
  getSkill: (id) => fetch(`${AGENT_URL}/skills/${id}`).then(r => r.json()),
  createSkill: (data, userId) => fetch(`${AGENT_URL}/skills${userId ? `?user_id=${userId}` : ''}`, {
    method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(data),
  }).then(r => r.json()),
  updateSkill: (id, data) => fetch(`${AGENT_URL}/skills/${id}`, {
    method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(data),
  }).then(r => r.json()),
  deleteSkill: (id) => fetch(`${AGENT_URL}/skills/${id}`, { method: 'DELETE' }).then(r => r.json()),
  forkSkill: (id, userId) => fetch(`${AGENT_URL}/skills/${id}/fork?user_id=${userId}`, {
    method: 'POST',
  }).then(r => r.json()),
  resetSkill: (name, userId) => fetch(`${AGENT_URL}/skills/name/${name}/reset?user_id=${userId}`, {
    method: 'DELETE',
  }).then(r => r.json()),

  // Sync Reconciliation
  getSyncReconciliationRuns: async (limit = 20, offset = 0) => {
    const res = await fetch(`${DATA_ENGINE_URL}/api/sync-reconciliation/runs?limit=${limit}&offset=${offset}`);
    if (!res.ok) throw new Error(`Failed to fetch runs: ${res.status}`);
    const json = await res.json();
    return json.data;
  },

  getSyncReconciliationRun: async (runId) => {
    const res = await fetch(`${DATA_ENGINE_URL}/api/sync-reconciliation/runs/${runId}`);
    if (!res.ok) throw new Error(`Failed to fetch run: ${res.status}`);
    const json = await res.json();
    return json.data;
  },

  getSyncReconciliationResults: async (runId, status = null) => {
    let url = `${DATA_ENGINE_URL}/api/sync-reconciliation/runs/${runId}/results`;
    if (status) url += `?status=${status}`;
    const res = await fetch(url);
    if (!res.ok) throw new Error(`Failed to fetch results: ${res.status}`);
    const json = await res.json();
    return json.data;
  },

  getLatestReconciliationResults: async (rfId) => {
    const res = await fetch(`${DATA_ENGINE_URL}/api/sync-reconciliation/results/${rfId}/latest`);
    if (!res.ok) throw new Error(`Failed to fetch latest results: ${res.status}`);
    const json = await res.json();
    return json.data;
  },

  getLatestSnapshot: async (rfId, reportType = 'PNL') => {
    const res = await fetch(`${DATA_ENGINE_URL}/api/sync-reconciliation/snapshots/${rfId}/latest?report_type=${reportType}`);
    if (!res.ok) throw new Error(`Failed to fetch snapshot: ${res.status}`);
    const json = await res.json();
    return json.data;
  },

  triggerSyncAll: async (rfIds = null) => {
    const res = await fetch(`${DATA_ENGINE_URL}/api/pipeline/sync-all`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(rfIds ? { rf_ids: rfIds } : {}),
    });
    if (!res.ok) throw new Error(`Failed to trigger sync-all: ${res.status}`);
    const json = await res.json();
    return json.data;
  },
};
